import { QuestionCircleOutlined } from "@ant-design/icons";
import { Checkbox, Form, Modal, Select, Tooltip, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { DATE_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { AktDateRangePicker } from "components/aktDatePicker";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useCreateCreditorReportMutation } from "features/documentCenter/documentCenterAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import moment from "moment-timezone";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 375px;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  margin-left: 4px;
`;

function CreateCreditorReportModal({ open, onOk, onCancel }) {
  const [form] = useForm();
  const { data: creditors, isLoading: isLoadingCreditors } = useFetchCreditorSummariesQuery();
  const { data: constants, isLoading: isLoadingConstants } = useFetchBackendConstantsQuery();
  const [createCreditorReport] = useCreateCreditorReportMutation();

  const onSubmit = async () => {
    const values = await form.validateFields();
    const {
      creditorId,
      reportType,
      dateRange,
      principalOnly,
      isExcludeCategoriesOn100PercentCommission,
    } = values;
    const [startDate, endDate] = dateRange;

    try {
      const result = await createCreditorReport({
        creditorId,
        reportType,
        startDate,
        endDate,
        ...(reportType === "epic_remittance" &&
          principalOnly && { paymentCategories: ["principal"] }),
        ...(reportType === "epic_remittance" &&
          isExcludeCategoriesOn100PercentCommission && {
            isExcludeCategoriesOn100PercentCommission: true,
          }),
      });

      await onOk();
      form.resetFields();
      return message.success(
        "Report is being generated. Please check back later in the Generated Documents section.",
      );
    } catch (error) {
      form.setFields(formatError(error));
    }
  };

  const disabledAfterTodayDate = (current) => {
    const today = moment().endOf("day");
    return current && current > today;
  };

  return (
    <Modal
      maskClosable={false}
      title="Create Creditor Report File"
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        layout="vertical"
        form={form}
        initialValues={{
          dateRange: [
            moment().startOf("month").format(DATE_FORMAT),
            moment().endOf("day").format(DATE_FORMAT),
          ],
        }}
      >
        <Form.Item
          label="Report Type"
          name="reportType"
          rules={[{ required: true, message: "Select a report type" }]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
            }
            disabled={isLoadingConstants}
            loading={isLoadingConstants}
            placeholder="Select a report type..."
            allowClear
            maxTagTextLength={20}
            options={constants?.creditorReportTypes?.map((constant) => ({
              label: constant.display,
              value: constant.value,
            }))}
          />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.reportType !== curr.reportType}>
          {({ getFieldValue }) =>
            getFieldValue("reportType") === "epic_remittance" && (
              <>
                <Form.Item name="principalOnly" valuePropName="checked" initialValue={false}>
                  <Checkbox>
                    <span>Principal payments only</span>
                    <Tooltip title="When checked, only the principal portion of each payment contributes to the row's amount. When unchecked, the entire payment allocation contributes to the row's amount.">
                      <StyledQuestionCircleOutlined />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  name="isExcludeCategoriesOn100PercentCommission"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Checkbox>
                    <span>Exclude 100% Commission Payment Categories</span>
                    <Tooltip title="Selecting this option will filter the payment line items to include only the balance categories that are shared with the client (less than 100% commission).">
                      <StyledQuestionCircleOutlined />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </>
            )
          }
        </Form.Item>
        <Form.Item
          label="Client"
          name="creditorId"
          rules={[{ required: true, message: "Select a creditor" }]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
            }
            disabled={isLoadingCreditors}
            loading={isLoadingCreditors}
            placeholder="Select a creditor..."
            options={creditors?.map((creditor) => ({
              label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
              value: creditor.id,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: "Select date range" }]}
        >
          <AktDateRangePicker disabledDate={disabledAfterTodayDate} picker="date" allowClear />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}

export default CreateCreditorReportModal;
